import { IntlProvider } from "react-intl";
import axios from "axios";
import {
  useState,
  createContext,
  useMemo,
  type PropsWithChildren,
  startTransition,
} from "react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import defaultRichTextElements from "../../utils/defaultRichTextElements";

import { type MessageFormatElement } from "react-intl";

export type Locales = {
  locale: "en" | "sv";
};

export type Messages =
  | Record<string, string>
  | Record<string, MessageFormatElement[]>
  | undefined;

export type Languages = { [key in Locales["locale"]]: Messages };

type TranslationKeys = Record<string, string>;
type LanguageContextType = {
  language: Locales["locale"];
  changeLanguage: (languageCode: Locales["locale"]) => void;
};

const translationFilesClient = axios.create({
  baseURL:
    "https://lokalise-translation-files.s3.eu-west-1.amazonaws.com/pension-onboarding/locale/",
  headers: {
    "Content-Type": "application/json",
  },
});

const fetchTranslatedMessages = async (lang: string) => {
  const response = translationFilesClient({
    url: `${lang}.json`,
    method: "GET",
  });

  return (await response).data as TranslationKeys;
};

const fallbackLanguageCode: Locales["locale"] = "sv";
const languageCode: Locales["locale"] =
  (localStorage.getItem("lang") as Locales["locale"]) ??
  (import.meta.env.VITE_LANGUAGE as Locales["locale"]) ??
  fallbackLanguageCode;

export const LanguageContext = createContext<LanguageContextType>({
  language: languageCode,
  changeLanguage: () => null,
});

export const LanguageProvider = ({ children }: PropsWithChildren) => {
  const [language, setLanguage] = useState(languageCode);
  const { data: translatedMessages } = useSuspenseQuery({
    queryKey: ["translations", language],
    queryFn: async () => fetchTranslatedMessages(language),
  });
  const queryClient = useQueryClient();

  async function changeLanguage(languageCode: Locales["locale"]) {
    await queryClient.prefetchQuery({
      queryKey: ["translations", languageCode],
      queryFn: async () => fetchTranslatedMessages(languageCode),
    });

    startTransition(() => {
      setLanguage(languageCode);
      localStorage.setItem("lang", languageCode);
    });
  }

  const context = useMemo(
    () => ({
      language,
      changeLanguage,
    }),
    [language]
  );
  return (
    <LanguageContext.Provider value={context}>
      <IntlProvider
        wrapRichTextChunksInFragment
        locale={language}
        defaultLocale="en"
        messages={translatedMessages}
        defaultRichTextElements={defaultRichTextElements}
        onError={(err) => {
          if (err.code.toString() === "MISSING_TRANSLATION") {
            return;
          }

          throw err;
        }}
        onWarn={(warn) => {
          if (warn.includes("pre-compile your messages for performance")) {
            return;
          }

          console.warn(warn);
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};
